/**
 * Used to specify repeated placements along the X+ axis in a single build command.
 * `101` = place 2 objects with a space between. Cannot exceed 64 bits.
 */
export class BuildBits {

	int: bigint


	constructor(input?: string | number | bigint) {
		if (input == null) {
			this.int = 0n
			return
		}
		if (typeof input == "string") {
			input = "0b" + reverse(input)
		} else if (typeof input != "number" && typeof input != "bigint") {
			throw new TypeError("input must be a number, bigint or string")
		}
		this.int = BigInt(input)
	}


	set(index: number): this {
		if (index < 0 || index > 63) throw new RangeError("index must be between [0,63]")
		this.int |= mask(index)
		return this
	}


	clear(index: number): this {
		if (index < 0 || index > 63) throw new RangeError("index must be between [0,63]")
		this.int &= ~mask(index)
		return this
	}


	toggle(index: number, force?: boolean): this {
		if (index < 0 || index > 63) throw new RangeError("index must be between [0,63]")
		if (typeof force == "undefined")
			this.int ^= mask(index)
		else if (force === true)
			this.set(index)
		else if (force === false)
			this.clear(index)
		return this
	}


	isSet(index: number): boolean {
		if (index < 0 || index > 63) return false
		return !!(this.int & mask(index))
	}


	isZero(): boolean {
		return this.int == 0n
	}


	isOne(): boolean {
		return this.int == 1n
	}


	trimLeadZeros(): this {
		if (this.int)
			this.int /= -this.int & this.int
		return this
	}


	get size(): number {
		return this.int.toString(2).length
	}

	*[Symbol.iterator](): Iterator<boolean> {
		for (const b of reverse(this.int.toString(2))) {
			yield b == "1"
		}
	}


	toArray(): boolean[] {
		return Array.from(this)
	}


	toString(): string {
		return reverse(this.int.toString(2))
	}


	get [Symbol.toStringTag]() {
		return this.toString()
	}


	equals(target: BuildBits) {
		return this.int === target?.int
	}


	clone(): BuildBits {
		return Object.assign(Object.create(Object.getPrototypeOf(this)), this)
	}
}

function mask(i: number) {
	return 1n << BigInt(i)
}

function reverse(str: string) {
	if (str.length < 1) return str
	return str.split("").reduce((r, c) => c + r)
}
